"use client";

import React, { useEffect, useState, useContext } from "react";
import { Modal, Card, Spinner, Button, Alert } from "react-bootstrap";
import { TariffInfo, TariffPriceInfo } from "../[wheelId]/tariffs/types";
import { UserContext } from "../components/auth/auth";
import { getCsrfToken } from "../components/utils/csrf";
import { useReadonly } from "../contexts/readonlyContext";

interface TariffsModalProps {
    show: boolean;
    onHide: () => void;
}

type Step = "list" | "payment" | "bonus_result";

export const TariffsModal: React.FC<TariffsModalProps> = ({ show, onHide }) => {
    const [tariffs, setTariffs] = useState<TariffInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const user = useContext(UserContext);
    const { setForceReadonly } = useReadonly();

    const [step, setStep] = useState<Step>("list");
    const [selectedPrice, setSelectedPrice] = useState<TariffPriceInfo | null>(null);
    const [selectedTariff, setSelectedTariff] = useState<TariffInfo | null>(null);
    const [bonusLoading, setBonusLoading] = useState(false);
    const [bonusResult, setBonusResult] = useState<{ success: boolean; message: string } | null>(null);

    useEffect(() => {
        if (show) {
            setStep("list");
            setSelectedPrice(null);
            setSelectedTariff(null);
            setBonusResult(null);
            setBonusLoading(false);
        }
    }, [show]);

    useEffect(() => {
        const fetchTariffs = async () => {
            try {
                const response = await fetch("/api/tariffs");
                if (response.ok) {
                    const data: TariffInfo[] = await response.json();
                    setTariffs(data);
                } else if (response.status === 401) {
                    setError("Для просмотра тарифов необходимо авторизоваться");
                } else {
                    setError("Не удалось загрузить тарифы");
                }
            } catch {
                setError("Ошибка сети. Попробуйте позже.");
            } finally {
                setLoading(false);
            }
        };

        fetchTariffs();
    }, []);

    const formatCost = (cost: number) => {
        return new Intl.NumberFormat("ru-RU", {
            style: "currency",
            currency: "RUB",
            minimumFractionDigits: 0,
        }).format(cost);
    };

    const handleContinueReadonly = () => {
        localStorage.setItem('tariffs_modal_dismissed', 'true');
        onHide();
    };

    const handleContinue = (tariff: TariffInfo, price: TariffPriceInfo) => {
        setSelectedTariff(tariff);
        setSelectedPrice(price);
        setStep("payment");
    };

    const handlePayByBonus = async () => {
        if (!selectedPrice) return;
        setBonusLoading(true);
        setBonusResult(null);

        try {
            const response = await fetch(`/api/payment/pay-by-bonus/${selectedPrice.id}`, {
                method: "POST",
                headers: {
                    "X-Csrftoken": getCsrfToken(),
                },
            });

            const data = await response.json();
            if (data.success && data.payed_till) {
                // Обновляем пользователя — readonly пересчитается
                user.updateUser({ ...user, payed_till: data.payed_till });
            }
            setBonusResult({ success: data.success, message: data.message || (data.success ? "Оплата прошла успешно!" : "Произошла ошибка") });
        } catch {
            setBonusResult({ success: false, message: "Ошибка сети. Попробуйте позже." });
        } finally {
            setBonusLoading(false);
            setStep("bonus_result");
        }
    };

    const handlePayByCard = () => {
        if (!selectedPrice || !selectedTariff) return;
        const email = user?.email || '';

        const widget = new ProdamusWidget({
            merchantId: '626166c4-3d34-41ba-a1c9-15601b6ab98e',
            salesChannelId: 'e25aafb9-1dbd-41be-a6d7-c06ba07656b0',
            currency: 'rub',
            products: [
                {
                    name: selectedPrice.title,
                    price: selectedPrice.price,
                    quantity: 1,
                },
            ],
            settings: {
                hasEmailInput: true,
                emailInputValue: email,
                hasPhoneInput: false,
            },
        });

        widget.open();
    };

    const handleBack = () => {
        if (step === "payment" || step === "bonus_result") {
            setStep("list");
            setSelectedPrice(null);
            setSelectedTariff(null);
            setBonusResult(null);
        }
    };

    const handleBonusResultClose = () => {
        if (bonusResult?.success) {
            // Сбрасываем readonly-состояние
            setForceReadonly(false);
            // Убираем всё, что связано с диалогом тарифов
            localStorage.removeItem('tariffs_modal_dismissed');
            localStorage.removeItem('force_readonly');
            onHide();
        } else {
            setStep("payment");
            setBonusResult(null);
        }
    };

    type TariffCard = { tariff: TariffInfo; price: TariffPriceInfo };
    const tariffCards: TariffCard[] = [];
    for (const tariff of tariffs) {
        const prices = tariff.prices || [];
        for (let idx = 0; idx < prices.length; idx++) {
            tariffCards.push({ tariff, price: prices[idx] });
        }
    }

    const renderList = () => (
        <div className="d-flex flex-column flex-md-row gap-3 overflow-auto">
            {tariffCards.map(({ tariff, price }) => (
                <Card
                    key={`${tariff.id}-${price.id}`}
                    className="flex-shrink-0"
                    style={{ minWidth: '280px', maxWidth: '320px' }}
                >
                    <Card.Header className="text-center bg-body-tertiary">
                        <div className="fw-bold">{price.title}</div>
                    </Card.Header>
                    <Card.Body className="d-flex flex-column align-items-center text-center">
                        <div className="mb-2">
                            <div className="display-6 fw-bold text-primary">
                                {formatCost(price.price)}
                            </div>
                        </div>
                        <div className="mt-auto w-100">
                            {price.price === 0 ? (
                                <Button
                                    variant="primary"
                                    className="w-100"
                                    onClick={handleContinueReadonly}
                                >
                                    Продолжить в режиме чтения
                                </Button>
                            ) : (
                                <Button
                                    variant="primary"
                                    className="w-100"
                                    onClick={() => handleContinue(tariff, price)}
                                >
                                    Продолжить
                                </Button>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            ))}
        </div>
    );

    const renderPayment = () => {
        if (!selectedPrice || !selectedTariff) return null;
        return (
            <div className="text-center">
                <h5 className="mb-1">{selectedPrice.title}</h5>
                <div className="display-6 fw-bold text-primary mb-4">{formatCost(selectedPrice.price)}</div>

                <div className="d-flex flex-column gap-3" style={{ maxWidth: 400, margin: '0 auto' }}>
                    {selectedPrice.bonus && (
                        <Button
                            variant="outline-warning"
                            size="lg"
                            className="w-100"
                            onClick={handlePayByBonus}
                            disabled={bonusLoading}
                        >
                            {bonusLoading ? "Оплата..." : "Оплатить бонусами"}
                        </Button>
                    )}
                    {selectedPrice.payment_url && (
                        <Button
                            variant="primary"
                            size="lg"
                            className="w-100"
                            onClick={handlePayByCard}
                        >
                            Оплатить картой
                        </Button>
                    )}
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mt-2"
                        onClick={handleBack}
                    >
                        Назад
                    </Button>
                </div>
            </div>
        );
    };

    const renderBonusResult = () => {
        if (!bonusResult) return null;
        return (
            <div className="text-center py-4">
                <div className={`display-6 fw-bold mb-3 ${bonusResult.success ? 'text-success' : 'text-danger'}`}>
                    {bonusResult.success ? '✓' : '✕'}
                </div>
                <p className="mb-4">{bonusResult.message}</p>
                <div className="d-flex flex-column gap-2" style={{ maxWidth: 300, margin: '0 auto' }}>
                    <Button
                        variant="primary"
                        className="w-100"
                        onClick={handleBonusResultClose}
                    >
                        {bonusResult.success ? "Закрыть" : "Попробовать снова"}
                    </Button>
                    {!bonusResult.success && selectedPrice?.payment_url && (
                        <Button
                            variant="outline-primary"
                            className="w-100"
                            onClick={handlePayByCard}
                        >
                            Оплатить картой
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <Modal show={show} onHide={() => { }} size="xl" centered>
            <Modal.Header>
                <Modal.Title>Тарифы</Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex justify-content-center">
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : error ? (
                    <div className="text-danger text-center">{error}</div>
                ) : step === "list" ? (
                    renderList()
                ) : step === "payment" ? (
                    renderPayment()
                ) : step === "bonus_result" ? (
                    renderBonusResult()
                ) : null}
            </Modal.Body>
        </Modal>
    );
};